import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { 
  CheckCircle, Download, Loader2, Music, Video, BookOpen, 
  Calendar, Ticket, Printer, Mail, Copy, ExternalLink,
  FileText, CreditCard, Clock, MapPin
} from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PurchaseConfirmation = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [purchaseData, setPurchaseData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [copied, setCopied] = useState(false);
  const maxAttempts = 10;

  const sessionId = searchParams.get('session_id');
  const purchaseType = searchParams.get('type') || detectPurchaseType(location.pathname);

  function detectPurchaseType(path) {
    if (path.includes('music') || path.includes('library')) return 'music';
    if (path.includes('video') || path.includes('ppv')) return 'video';
    if (path.includes('ebook') || path.includes('book')) return 'ebook';
    if (path.includes('class') || path.includes('schedule')) return 'class';
    if (path.includes('course')) return 'course';
    if (path.includes('membership')) return 'membership';
    return 'general';
  }

  useEffect(() => {
    if (!sessionId) {
      // Check if we have purchase data in location state
      if (location.state?.purchaseData) {
        setPurchaseData(location.state.purchaseData);
        setStatus('success');
      } else {
        navigate('/');
      }
      return;
    }
    checkPaymentStatus();
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API}/purchase/verify/${sessionId}`);
      
      if (response.data.status === 'paid' || response.data.payment_status === 'paid') {
        setStatus('success');
        setPurchaseData(response.data);
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else if (attempts < maxAttempts) {
        setAttempts(prev => prev + 1);
        setTimeout(checkPaymentStatus, 2000);
      } else {
        setStatus('timeout');
      }
    } catch (error) {
      // Try fallback endpoint
      try {
        const fallback = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (fallback.data.payment_status === 'paid') {
          setStatus('success');
          setPurchaseData(fallback.data);
        } else if (attempts < maxAttempts) {
          setAttempts(prev => prev + 1);
          setTimeout(checkPaymentStatus, 2000);
        } else {
          setStatus('timeout');
        }
      } catch (e) {
        if (attempts < maxAttempts) {
          setAttempts(prev => prev + 1);
          setTimeout(checkPaymentStatus, 2000);
        } else {
          setStatus('error');
        }
      }
    }
  };

  const copyConfirmationNumber = () => {
    const confirmNum = purchaseData?.confirmation_number || sessionId?.slice(-12).toUpperCase();
    navigator.clipboard.writeText(confirmNum);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const printReceipt = () => {
    window.print();
  };

  const getConfirmationNumber = () => {
    return purchaseData?.confirmation_number || sessionId?.slice(-12).toUpperCase() || 'N/A';
  };

  const getCurrentDate = () => {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-stone-950 flex flex-col items-center justify-center px-6">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        >
          <Loader2 className="w-16 h-16 text-amber-500 mb-6" />
        </motion.div>
        <h2 className="text-2xl font-bold text-white mb-2">Processing Your Purchase...</h2>
        <p className="text-gray-400">Please wait while we confirm your payment</p>
        <p className="text-gray-500 text-sm mt-4">Attempt {attempts + 1} of {maxAttempts}</p>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-stone-950 py-12 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Success Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle className="w-14 h-14 text-white" />
            </motion.div>
            <h1 className="text-4xl font-black text-white mb-2">Purchase Successful!</h1>
            <p className="text-gray-400 text-lg">Thank you for your purchase from Escola de Samba Casa Samba</p>
          </motion.div>

          {/* Confirmation Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-stone-900 border border-stone-800 rounded-2xl overflow-hidden mb-6 print:bg-white print:text-black"
          >
            {/* Confirmation Header */}
            <div className="bg-gradient-to-r from-amber-500 to-yellow-500 px-6 py-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-black/70 text-sm font-medium">Confirmation Number</p>
                  <p className="text-black text-2xl font-black tracking-wider">{getConfirmationNumber()}</p>
                </div>
                <button
                  onClick={copyConfirmationNumber}
                  className="p-2 bg-black/20 hover:bg-black/30 rounded-lg transition-colors"
                  title="Copy confirmation number"
                >
                  {copied ? <CheckCircle className="w-5 h-5 text-black" /> : <Copy className="w-5 h-5 text-black" />}
                </button>
              </div>
            </div>

            {/* Purchase Details */}
            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <p className="text-gray-500 text-sm">Purchase Date</p>
                    <p className="text-white font-medium">{getCurrentDate()}</p>
                  </div>
                  
                  {purchaseData?.customer_email && (
                    <div>
                      <p className="text-gray-500 text-sm">Email</p>
                      <p className="text-white font-medium">{purchaseData.customer_email}</p>
                    </div>
                  )}

                  {purchaseData?.item_name && (
                    <div>
                      <p className="text-gray-500 text-sm">Item Purchased</p>
                      <p className="text-white font-medium">{purchaseData.item_name}</p>
                    </div>
                  )}
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <p className="text-gray-500 text-sm">Amount Paid</p>
                    <p className="text-3xl font-black text-amber-500">
                      ${(purchaseData?.amount || 0).toFixed(2)}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-gray-500 text-sm">Payment Status</p>
                    <span className="inline-flex items-center gap-2 px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-medium">
                      <CheckCircle className="w-4 h-4" />
                      Paid
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Action Cards Based on Purchase Type */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="space-y-4"
          >
            {/* Digital Content - Download Section */}
            {(purchaseType === 'music' || purchaseType === 'video' || purchaseType === 'ebook' || purchaseType === 'general') && (
              <div className="bg-stone-900 border border-amber-500/30 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Download className="w-5 h-5 text-amber-500" />
                  Access Your Content
                </h3>
                <p className="text-gray-400 mb-4">Your digital content is ready! Access it immediately:</p>
                
                <div className="grid sm:grid-cols-2 gap-3">
                  <button
                    onClick={() => navigate('/library')}
                    className="flex items-center justify-center gap-2 px-6 py-4 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-xl transition-all"
                  >
                    <Music className="w-5 h-5" />
                    My Music Library
                  </button>
                  <button
                    onClick={() => navigate('/my-videos')}
                    className="flex items-center justify-center gap-2 px-6 py-4 bg-stone-800 hover:bg-stone-700 text-white font-bold rounded-xl transition-all border border-stone-700"
                  >
                    <Video className="w-5 h-5" />
                    My Videos
                  </button>
                </div>

                {purchaseType === 'ebook' && (
                  <button
                    onClick={() => navigate('/kapoeira-book')}
                    className="w-full mt-3 flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-bold rounded-xl transition-all hover:opacity-90"
                  >
                    <BookOpen className="w-5 h-5" />
                    Read E-Book Now
                  </button>
                )}
              </div>
            )}

            {/* Class Purchase - Class Pass */}
            {purchaseType === 'class' && (
              <div className="bg-stone-900 border border-green-500/30 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Ticket className="w-5 h-5 text-green-500" />
                  Your Class Pass
                </h3>
                
                <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-green-400 font-bold text-lg">CLASS PASS</span>
                    <span className="text-white font-mono text-sm">{getConfirmationNumber()}</span>
                  </div>
                  <p className="text-white font-medium">{purchaseData?.class_name || 'Escola de Samba Casa Samba Class'}</p>
                  {purchaseData?.class_date && (
                    <div className="flex items-center gap-2 text-gray-400 text-sm mt-2">
                      <Calendar className="w-4 h-4" />
                      {purchaseData.class_date}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-gray-400 text-sm mt-1">
                    <MapPin className="w-4 h-4" />
                    N.O.R.D. Annunciation Center, 800 Race St, New Orleans
                  </div>
                </div>

                <p className="text-gray-400 text-sm mb-4">
                  Show this confirmation number when you arrive at class. You can also print this page as your class pass.
                </p>

                <button
                  onClick={printReceipt}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-green-500 hover:bg-green-400 text-black font-bold rounded-xl transition-all"
                >
                  <Printer className="w-5 h-5" />
                  Print Class Pass
                </button>
              </div>
            )}

            {/* Course Purchase */}
            {purchaseType === 'course' && (
              <div className="bg-stone-900 border border-purple-500/30 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-purple-500" />
                  Access Your Course
                </h3>
                <p className="text-gray-400 mb-4">Your course enrollment is confirmed! Start learning immediately:</p>
                
                <button
                  onClick={() => navigate('/courses')}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-purple-500 hover:bg-purple-400 text-white font-bold rounded-xl transition-all"
                >
                  <ExternalLink className="w-5 h-5" />
                  Go to My Courses
                </button>
              </div>
            )}

            {/* Membership Purchase */}
            {purchaseType === 'membership' && (
              <div className="bg-stone-900 border border-blue-500/30 rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-blue-500" />
                  Membership Activated
                </h3>
                <p className="text-gray-400 mb-4">Your membership is now active! Enjoy all member benefits:</p>
                
                <button
                  onClick={() => navigate('/membership')}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-blue-500 hover:bg-blue-400 text-white font-bold rounded-xl transition-all"
                >
                  <ExternalLink className="w-5 h-5" />
                  View Membership Benefits
                </button>
              </div>
            )}

            {/* Receipt Actions */}
            <div className="bg-stone-900 border border-stone-800 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-gray-400" />
                Receipt & Records
              </h3>
              
              <div className="grid sm:grid-cols-2 gap-3">
                <button
                  onClick={printReceipt}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-stone-800 hover:bg-stone-700 text-white font-medium rounded-xl transition-all border border-stone-700"
                >
                  <Printer className="w-5 h-5" />
                  Print Receipt
                </button>
                <button
                  onClick={() => navigate('/account/purchases')}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-stone-800 hover:bg-stone-700 text-white font-medium rounded-xl transition-all border border-stone-700"
                >
                  <Clock className="w-5 h-5" />
                  Purchase History
                </button>
              </div>
              
              <p className="text-gray-500 text-sm mt-4 text-center">
                A confirmation email has been sent to your email address.
              </p>
            </div>
          </motion.div>

          {/* Continue Shopping */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center mt-8"
          >
            <button
              onClick={() => navigate('/')}
              className="text-amber-400 hover:text-amber-300 font-medium transition-colors"
            >
              ← Continue Shopping
            </button>
          </motion.div>
        </div>

        {/* Print Styles */}
        <style>{`
          @media print {
            body { background: white !important; }
            .print\\:bg-white { background: white !important; }
            .print\\:text-black { color: black !important; }
            button { display: none !important; }
          }
        `}</style>
      </div>
    );
  }

  // Error states
  return (
    <div className="min-h-screen bg-stone-950 flex flex-col items-center justify-center px-6 text-center">
      <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mb-6">
        <CreditCard className="w-10 h-10 text-red-400" />
      </div>
      <h2 className="text-3xl font-bold text-white mb-4">
        {status === 'expired' ? 'Session Expired' : 'Unable to Verify Payment'}
      </h2>
      <p className="text-gray-400 mb-8 max-w-md">
        {status === 'expired'
          ? 'Your payment session has expired. Please try your purchase again.'
          : 'We couldn\'t verify your payment status. If you were charged, please contact us with your confirmation number.'}
      </p>
      <div className="flex gap-4">
        <button
          onClick={() => navigate('/')}
          className="px-6 py-3 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-xl transition-all"
        >
          Back to Store
        </button>
        <button
          onClick={() => navigate('/help')}
          className="px-6 py-3 bg-stone-800 hover:bg-stone-700 text-white font-medium rounded-xl transition-all border border-stone-700"
        >
          Contact Support
        </button>
      </div>
    </div>
  );
};

export default PurchaseConfirmation;
